/**
 * CalendarView - Calendar with integrated "Oggi" style detail view
 *
 * Architecture:
 * - Sidebar: Event type filters (meeting, task, payment, reminder, deadline, etc.)
 * - Main area top: Calendar grid with navigation and view/gantt switch
 * - Main area bottom: "Oggi" style grouped entities for selected date
 */

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { renderEntityRow } from '../components/common/registry/ComponentRouter';
import { useEntityModal } from '../hooks/useEntityModal';
import { ContactWidget } from '../components/hub/ContactWidget';
import { useAppShell } from '../layouts/AppShell';
import { useTranslation } from '../hooks';
import {
  useCalendarEvents,
  useProjectsStore,
  useHubStore,
  useInvoices,
  useMovements,
} from '@business-assistant/shared';
import { useCalendarStore } from '@business-assistant/shared/stores';
import { getToday, DEMO_TODAY } from '@business-assistant/shared-data';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  BarChart3,
  ListTodo,
  FolderKanban,
  Users,
  Phone,
  Mail,
  MessageCircle,
  FileText,
  Clock,
  Flag,
  Landmark,
  TrendingUp,
  TrendingDown,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  Bell,
  Target,
  CreditCard,
  PhoneIncoming,
  PhoneOutgoing,
  Plus,
} from 'lucide-react';
import { EventWidget } from '../components/calendar/EventWidget';

// =============================================================================
// TYPES
// =============================================================================

type ViewMode = 'month' | 'week' | 'day';
type DisplayMode = 'calendar' | 'gantt';

interface FilterType {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  entityTypes: string[]; // Which entity types to include
}

// =============================================================================
// FILTER DEFINITIONS
// =============================================================================

// Filter type definitions with i18n keys
const FILTER_TYPES: FilterType[] = [
  { id: 'all', label: 'calendar.eventTypes.all', icon: <Calendar size={16} />, color: '#6b7280', entityTypes: ['all'] },
  { id: 'meeting', label: 'calendar.eventTypes.meeting', icon: <Users size={16} />, color: '#8b5cf6', entityTypes: ['evt'] },
  { id: 'task', label: 'calendar.eventTypes.task', icon: <ListTodo size={16} />, color: '#3b82f6', entityTypes: ['tsk'] },
  { id: 'milestone', label: 'calendar.eventTypes.milestone', icon: <Flag size={16} />, color: '#ec4899', entityTypes: ['mst'] },
  { id: 'deadline', label: 'calendar.eventTypes.deadline', icon: <Target size={16} />, color: '#ef4444', entityTypes: ['deadline'] },
  { id: 'reminder', label: 'calendar.eventTypes.reminder', icon: <Bell size={16} />, color: '#f59e0b', entityTypes: ['reminder'] },
  { id: 'payment', label: 'calendar.eventTypes.payment', icon: <CreditCard size={16} />, color: '#10b981', entityTypes: ['inv', 'mov'] },
  { id: 'call', label: 'calendar.eventTypes.call', icon: <Phone size={16} />, color: '#1D5BBF', entityTypes: ['call'] },
  { id: 'email', label: 'calendar.eventTypes.email', icon: <Mail size={16} />, color: '#FF6B2D', entityTypes: ['email'] },
  { id: 'message', label: 'calendar.eventTypes.message', icon: <MessageCircle size={16} />, color: '#25D366', entityTypes: ['msg', 'whatsapp', 'telegram'] },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function isSameDay(d1: Date, d2: Date): boolean {
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

function formatDateKey(date: Date): string {
  return date.toISOString().split('T')[0];
}

function isDateInRange(dateStr: string | undefined, targetDate: Date): boolean {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return isSameDay(date, targetDate);
}

// =============================================================================
// COMPONENT
// =============================================================================

export function CalendarView() {
  const navigate = useNavigate();
  const { activeFilters, setDrawerSectionsOverride, setActiveFilter, showToast } = useAppShell();
  const selectedFilter = activeFilters.calendar || 'all';
  const { t, language } = useTranslation();

  // View state (UI-only state from old store)
  const selectedDateStore = useCalendarStore((s) => s.selectedDate);
  const viewModeStore = useCalendarStore((s) => s.viewMode);
  const setSelectedDateStore = useCalendarStore((s) => s.setSelectedDate);
  const setViewModeStore = useCalendarStore((s) => s.setViewMode);

  const [viewMode, setViewMode] = useState<ViewMode>(viewModeStore || 'month');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('calendar');
  const [currentDate, setCurrentDate] = useState(() => new Date(getToday()));
  const [selectedDate, setSelectedDate] = useState(() => selectedDateStore ? new Date(selectedDateStore) : new Date(getToday()));

  // Entity modal via URL (centralized)
  const { openEntity } = useEntityModal();
  // Contact widget state (for avatar click)
  const [selectedContact, setSelectedContact] = useState<{ name: string; id?: string } | null>(null);
  // Event form state
  const [showEventForm, setShowEventForm] = useState(false);

  // Get data from stores and react-query
  const { data: calendarEvents = [] } = useCalendarEvents();
  const tasks = useProjectsStore((s) => s.tasks);
  const milestones = useProjectsStore((s) => s.milestones);
  const projects = useProjectsStore((s) => s.projects);
  const conversations = useHubStore((s) => s.conversations);
  const { data: invoices = [] } = useInvoices();
  const { data: movements = [] } = useMovements();

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (viewMode === 'month') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else if (viewMode === 'week') {
        newDate.setDate(newDate.getDate() - 7);
      } else {
        newDate.setDate(newDate.getDate() - 1);
      }
      return newDate;
    });
  }, [viewMode]);

  const goToNext = useCallback(() => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      if (viewMode === 'month') {
        newDate.setMonth(newDate.getMonth() + 1);
      } else if (viewMode === 'week') {
        newDate.setDate(newDate.getDate() + 7);
      } else {
        newDate.setDate(newDate.getDate() + 1);
      }
      return newDate;
    });
  }, [viewMode]);

  const goToToday = useCallback(() => {
    const today = new Date(getToday());
    setCurrentDate(today);
    setSelectedDate(today);
  }, []);

  // Get all entities for a specific date
  const getEntitiesForDate = useCallback((date: Date) => {
    const dateKey = formatDateKey(date);

    return {
      events: calendarEvents.filter((e) => isDateInRange(e.startAt, date)),
      tasks: tasks.filter((t) => isDateInRange(t.dueAt, date) || isDateInRange(t.startAt, date)),
      milestones: milestones.filter((m) => isDateInRange(m.dueDate, date)),
      invoices: invoices.filter((inv) => isDateInRange(inv.dueAt, date)),
      movements: movements.filter((m) => isDateInRange(m.at, date)),
      calls: conversations.filter((c) => c.channel === 'call' && isDateInRange(c.updatedAt, date)),
      emails: conversations.filter((c) => c.channel === 'email' && isDateInRange(c.updatedAt, date)),
      messages: conversations.filter((c) =>
        ['whatsapp', 'telegram', 'sms'].includes(c.channel || '') && isDateInRange(c.updatedAt, date)
      ),
    };
  }, [calendarEvents, tasks, milestones, invoices, movements, conversations]);

  // Count entities for a date based on current filter
  const getCountForDate = useCallback((date: Date) => {
    const entities = getEntitiesForDate(date);
    const filter = FILTER_TYPES.find((f) => f.id === selectedFilter) || FILTER_TYPES[0];

    if (filter.id === 'all') {
      return {
        events: entities.events.length,
        tasks: entities.tasks.length,
        milestones: entities.milestones.length,
        invoices: entities.invoices.length,
        calls: entities.calls.length,
        emails: entities.emails.length,
        total: entities.events.length + entities.tasks.length + entities.milestones.length +
          entities.invoices.length + entities.calls.length + entities.emails.length + entities.messages.length,
      };
    }

    // Filter-specific counts
    let total = 0;
    if (filter.entityTypes.includes('evt')) total += entities.events.length;
    if (filter.entityTypes.includes('tsk')) total += entities.tasks.length;
    if (filter.entityTypes.includes('mst')) total += entities.milestones.length;
    if (filter.entityTypes.includes('inv') || filter.entityTypes.includes('mov')) {
      total += entities.invoices.length + entities.movements.length;
    }
    if (filter.entityTypes.includes('call')) total += entities.calls.length;
    if (filter.entityTypes.includes('email')) total += entities.emails.length;
    if (filter.entityTypes.some((t) => ['msg', 'whatsapp', 'telegram'].includes(t))) {
      total += entities.messages.length;
    }

    return { total };
  }, [getEntitiesForDate, selectedFilter]);

  // Entities for selected date (for detail section)
  const selectedDateEntities = useMemo(() => {
    return getEntitiesForDate(selectedDate);
  }, [selectedDate, getEntitiesForDate]);

  // Group entities by module (like TodayView)
  const moduleGroups = useMemo(() => {
    const groups: any[] = [];
    const filter = FILTER_TYPES.find((f) => f.id === selectedFilter) || FILTER_TYPES[0];
    const showAll = filter.id === 'all';

    // PROGETTI (Tasks + Milestones)
    if (showAll || filter.entityTypes.some((t) => ['tsk', 'mst'].includes(t))) {
      const projectActivities = new Map<string, { tasks: any[]; milestones: any[] }>();

      // Group tasks by project
      if (showAll || filter.entityTypes.includes('tsk')) {
        selectedDateEntities.tasks.forEach((task) => {
          const projectId = task.projectId;
          if (!projectActivities.has(projectId)) {
            projectActivities.set(projectId, { tasks: [], milestones: [] });
          }
          projectActivities.get(projectId)!.tasks.push(task);
        });
      }

      // Group milestones by project
      if (showAll || filter.entityTypes.includes('mst')) {
        selectedDateEntities.milestones.forEach((milestone) => {
          const projectId = milestone.projectId;
          if (!projectActivities.has(projectId)) {
            projectActivities.set(projectId, { tasks: [], milestones: [] });
          }
          projectActivities.get(projectId)!.milestones.push(milestone);
        });
      }

      const projectsModule: any = {
        moduleName: t('nav.projects').toUpperCase(),
        moduleId: 'projects',
        icon: <FolderKanban size={20} />,
        color: '#8b5cf6',
        subgroups: [],
      };

      projectActivities.forEach((activities, projectId) => {
        const project = projects.find((p) => p.id === projectId);
        const allEntities = [...activities.tasks, ...activities.milestones];
        if (allEntities.length > 0 && project) {
          projectsModule.subgroups.push({
            title: project.title,
            projectId: project.id,
            icon: <FolderKanban size={16} />,
            color: '#8b5cf6',
            entities: allEntities,
          });
        }
      });

      if (projectsModule.subgroups.length > 0) {
        groups.push(projectsModule);
      }
    }

    // EVENTS
    if ((showAll || filter.entityTypes.includes('evt')) && selectedDateEntities.events.length > 0) {
      groups.push({
        moduleName: t('calendar.events').toUpperCase(),
        moduleId: 'calendar',
        icon: <Calendar size={20} />,
        color: '#7c3aed',
        subgroups: [{
          title: t('calendar.eventsOfDay'),
          icon: <Calendar size={16} />,
          color: '#7c3aed',
          entities: selectedDateEntities.events,
        }],
      });
    }

    // HUB (Calls, Emails, Messages)
    const hubModule: any = {
      moduleName: t('nav.hub').toUpperCase(),
      moduleId: 'hub',
      icon: <Users size={20} />,
      color: '#10b981',
      subgroups: [],
    };

    if ((showAll || filter.entityTypes.includes('call')) && selectedDateEntities.calls.length > 0) {
      hubModule.subgroups.push({
        title: t('hub.channels.calls'),
        icon: <Phone size={16} />,
        color: '#1D5BBF',
        entities: selectedDateEntities.calls,
      });
    }

    if ((showAll || filter.entityTypes.includes('email')) && selectedDateEntities.emails.length > 0) {
      hubModule.subgroups.push({
        title: t('hub.channels.email'),
        icon: <Mail size={16} />,
        color: '#FF6B2D',
        entities: selectedDateEntities.emails,
      });
    }

    if ((showAll || filter.entityTypes.some((type) => ['msg', 'whatsapp', 'telegram'].includes(type))) && selectedDateEntities.messages.length > 0) {
      hubModule.subgroups.push({
        title: t('calendar.messages'),
        icon: <MessageCircle size={16} />,
        color: '#25D366',
        entities: selectedDateEntities.messages,
      });
    }

    if (hubModule.subgroups.length > 0) {
      groups.push(hubModule);
    }

    // ACCOUNTING
    if (showAll || filter.entityTypes.some((type) => ['inv', 'mov'].includes(type))) {
      const accountingModule: any = {
        moduleName: t('nav.accounting').toUpperCase(),
        moduleId: 'accounting',
        icon: <FileText size={20} />,
        color: '#F59E0B',
        subgroups: [],
      };

      const invoicesToCollect = selectedDateEntities.invoices.filter((inv) => inv.direction === 'issued');
      const invoicesToPay = selectedDateEntities.invoices.filter((inv) => inv.direction === 'received');

      if (invoicesToCollect.length > 0) {
        accountingModule.subgroups.push({
          title: t('calendar.toCollect'),
          icon: <TrendingUp size={16} />,
          color: '#eab308',
          entities: invoicesToCollect,
        });
      }

      if (invoicesToPay.length > 0) {
        accountingModule.subgroups.push({
          title: t('calendar.toPay'),
          icon: <TrendingDown size={16} />,
          color: '#ef4444',
          entities: invoicesToPay,
        });
      }

      if (selectedDateEntities.movements.length > 0) {
        accountingModule.subgroups.push({
          title: t('calendar.bankMovements'),
          icon: <Landmark size={16} />,
          color: '#0891b2',
          entities: selectedDateEntities.movements,
        });
      }

      if (accountingModule.subgroups.length > 0) {
        groups.push(accountingModule);
      }
    }

    return groups;
  }, [selectedDateEntities, selectedFilter, projects, t]);

  // Calculate total count for sidebar
  const totalCounts = useMemo(() => {
    // Count for the visible range (month/week)
    const counts: Record<string, number> = {};
    FILTER_TYPES.forEach((f) => {
      counts[f.id] = 0;
    });

    // For sidebar, show counts for selected date
    const e = selectedDateEntities;
    counts['all'] = e.events.length + e.tasks.length + e.milestones.length + e.invoices.length + e.calls.length + e.emails.length + e.messages.length;
    counts['meeting'] = e.events.length;
    counts['task'] = e.tasks.length;
    counts['milestone'] = e.milestones.length;
    counts['payment'] = e.invoices.length + e.movements.length;
    counts['call'] = e.calls.length;
    counts['email'] = e.emails.length;
    counts['message'] = e.messages.length;

    return counts;
  }, [selectedDateEntities]);

  // Set up sidebar with event type filters
  useEffect(() => {
    const filterItems = FILTER_TYPES.map((filter) => ({
      id: filter.id,
      label: t(filter.label),
      hint: t(`calendar.hints.${filter.id}`),
      icon: filter.id === 'meeting' ? 'users' :
        filter.id === 'task' ? 'task' :
          filter.id === 'milestone' ? 'flag' :
            filter.id === 'deadline' ? 'target' :
              filter.id === 'reminder' ? 'bell' :
                filter.id === 'payment' ? 'wallet' :
                  filter.id === 'call' ? 'phone' :
                    filter.id === 'email' ? 'mail' :
                      filter.id === 'message' ? 'message' : 'calendar',
      count: totalCounts[filter.id] || 0,
      active: selectedFilter === filter.id,
      onClick: () => setActiveFilter('calendar', filter.id),
    }));

    setDrawerSectionsOverride([
      { title: t('calendar.eventTypes.title'), items: filterItems },
    ]);
  }, [selectedFilter, totalCounts, setActiveFilter, setDrawerSectionsOverride, t]);

  // Handle entity click - open via URL (GlobalEntityModal handles rendering)
  const handleEntityClick = useCallback((entity: any) => {
    if (entity?.id?.startsWith('prj-')) {
      navigate(`/projects/${entity.id}/dashboard`);
      return;
    }
    if (entity?.id) {
      openEntity(entity.id);
    }
  }, [navigate, openEntity]);

  // Handle action click
  const handleActionClick = useCallback((actionType: string, entityId: string) => {
    console.log('Action clicked:', actionType, entityId);
  }, []);

  // Handle avatar click
  const handleAvatarClick = useCallback((name: string, id?: string) => {
    setSelectedContact({ name, id });
  }, []);

  // Format date display based on current language
  const locale = language === 'it' ? 'it-IT' : 'en-US';
  const getDateDisplay = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      const dateStr = currentDate.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
      return t('calendar.weekOf', { date: dateStr });
    } else {
      return currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
  };

  return (
    <>
      <div style={styles.container}>
        {/* Calendar Section */}
        <div style={styles.calendarSection}>
          {/* Controls */}
          <div style={styles.controls}>
            {/* View mode switch */}
            <div style={styles.viewSwitch}>
              {(['month', 'week', 'day'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  style={{
                    ...styles.viewButton,
                    ...(viewMode === mode ? styles.viewButtonActive : {}),
                  }}
                >
                  {mode === 'month' && <CalendarDays size={16} />}
                  {mode === 'week' && <CalendarRange size={16} />}
                  {mode === 'day' && <CalendarClock size={16} />}
                  <span>{t(`calendar.views.${mode}`)}</span>
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div style={styles.navigation}>
              <button onClick={goToPrevious} style={styles.navButton}>
                <ChevronLeft size={20} />
              </button>
              <button onClick={goToToday} style={styles.todayButton}>
                {t('common.today')}
              </button>
              <button onClick={goToNext} style={styles.navButton}>
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Display mode switch */}
            <div style={styles.displaySwitch}>
              <button
                onClick={() => setDisplayMode('calendar')}
                style={{
                  ...styles.displayButton,
                  ...(displayMode === 'calendar' ? styles.displayButtonActive : {}),
                }}
              >
                <Calendar size={16} />
                <span>{t('calendar.switch.calendar')}</span>
              </button>
              <button
                onClick={() => setDisplayMode('gantt')}
                style={{
                  ...styles.displayButton,
                  ...(displayMode === 'gantt' ? styles.displayButtonActive : {}),
                }}
              >
                <BarChart3 size={16} />
                <span>{t('calendar.switch.gantt')}</span>
              </button>
            </div>

            {/* Add event button */}
            <button
              onClick={() => setShowEventForm(true)}
              style={styles.addEventButton}
              title={t('events.newEvent')}
            >
              <Plus size={18} />
              <span>{t('events.newEvent')}</span>
            </button>
          </div>

          {/* Date display */}
          <div style={styles.dateDisplay}>
            {getDateDisplay()}
          </div>

          {/* Calendar Grid */}
          {displayMode === 'calendar' && viewMode === 'month' && (
            <MonthGrid
              currentDate={currentDate}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              getCountForDate={getCountForDate}
              selectedFilter={selectedFilter}
              locale={locale}
            />
          )}

          {displayMode === 'calendar' && viewMode === 'week' && (
            <WeekGrid
              currentDate={currentDate}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              getCountForDate={getCountForDate}
              getEntitiesForDate={getEntitiesForDate}
              locale={locale}
            />
          )}

          {displayMode === 'calendar' && viewMode === 'day' && (
            <DayGrid
              currentDate={currentDate}
              getEntitiesForDate={getEntitiesForDate}
            />
          )}

          {displayMode === 'gantt' && (
            <div style={styles.ganttPlaceholder}>
              <BarChart3 size={48} style={{ color: '#9ca3af' }} />
              <p>{t('calendar.ganttComingSoon')}</p>
            </div>
          )}
        </div>

        {/* Selected Date Detail Section */}
        <div style={styles.detailSection}>
          <div style={styles.detailHeader}>
            <Calendar size={20} />
            <span>
              {selectedDate.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' })}
            </span>
          </div>

          <div style={styles.detailContent}>
            {moduleGroups.length === 0 ? (
              <div style={styles.emptyState}>
                <p>{t('calendar.noItemsForDate')}</p>
              </div>
            ) : (
              moduleGroups.map((module) => {
                const moduleTotal = module.subgroups.reduce(
                  (sum: number, sg: any) => sum + sg.entities.length, 0
                );

                return (
                  <section key={module.moduleId} style={styles.moduleSection}>
                    <div style={styles.moduleHeader}>
                      <div style={styles.moduleTitle}>
                        <span style={{ ...styles.moduleIcon, color: module.color }}>
                          {module.icon}
                        </span>
                        <h2 style={{ ...styles.moduleName, color: module.color }}>
                          {module.moduleName}
                        </h2>
                        <span style={styles.moduleCount}>({moduleTotal})</span>
                      </div>
                    </div>

                    {module.subgroups.map((subgroup: any, idx: number) => (
                      <div key={idx} style={styles.subgroupSection}>
                        <div style={styles.subgroupHeader}>
                          <div style={styles.subgroupTitle}>
                            <span style={{ ...styles.subgroupIcon, color: subgroup.color }}>
                              {subgroup.icon}
                            </span>
                            <h3 style={styles.subgroupName}>{subgroup.title}</h3>
                            <span style={styles.subgroupCount}>({subgroup.entities.length})</span>
                          </div>
                        </div>

                        <div style={styles.subgroupContent}>
                          {subgroup.entities.map((entity: any) =>
                            renderEntityRow(entity, {
                              onTitleClick: handleEntityClick,
                              onActionClick: handleActionClick,
                              onAvatarClick: handleAvatarClick,
                            })
                          )}
                        </div>
                      </div>
                    ))}
                  </section>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Contact Widget - for avatar clicks */}
      {selectedContact && (
        <ContactWidget
          isOpen={true}
          contactId={selectedContact.id}
          onClose={() => setSelectedContact(null)}
          onSave={() => setSelectedContact(null)}
        />
      )}

      {/* Event Form */}
      {showEventForm && (
        <EventWidget
          isOpen={true}
          initialDate={formatDateKey(selectedDate)}
          onClose={() => setShowEventForm(false)}
          onSave={(event) => {
            showToast(t('events.eventCreated'), 'success');
          }}
        />
      )}
    </>
  );
}

// =============================================================================
// MONTH GRID COMPONENT
// =============================================================================

function MonthGrid({
  currentDate,
  selectedDate,
  onSelectDate,
  getCountForDate,
  selectedFilter,
  locale,
}: {
  currentDate: Date;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  getCountForDate: (date: Date) => any;
  selectedFilter: string;
  locale: string;
}) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const today = new Date(getToday());

  // Build calendar grid
  const firstDay = new Date(year, month, 1);
  const startingDayOfWeek = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const weeks: Date[][] = [];
  let currentWeek: Date[] = [];

  // Add previous month days
  for (let i = 0; i < startingDayOfWeek; i++) {
    currentWeek.push(new Date(year, month, -startingDayOfWeek + i + 1));
  }

  // Add days of month
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(new Date(year, month, day));
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }

  // Fill last week
  if (currentWeek.length > 0) {
    const remaining = 7 - currentWeek.length;
    for (let i = 1; i <= remaining; i++) {
      currentWeek.push(new Date(year, month + 1, i));
    }
    weeks.push(currentWeek);
  }

  // Generate weekday names based on locale
  const weekdayNames = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(2024, 0, i); // Jan 2024 starts on Monday (0 = Sun, 1 = Mon, etc.)
    return date.toLocaleDateString(locale, { weekday: 'short' });
  });

  return (
    <div style={styles.monthGrid}>
      {/* Weekday headers */}
      <div style={styles.weekdayHeaders}>
        {weekdayNames.map((day, idx) => (
          <div key={idx} style={styles.weekdayHeader}>{day}</div>
        ))}
      </div>

      {/* Calendar days */}
      {weeks.map((week, weekIdx) => (
        <div key={weekIdx} style={styles.week}>
          {week.map((date, dayIdx) => {
            const isCurrentMonth = date.getMonth() === month;
            const isToday = isSameDay(date, today);
            const isSelected = isSameDay(date, selectedDate);
            const counts = getCountForDate(date);

            return (
              <div
                key={dayIdx}
                onClick={() => onSelectDate(date)}
                style={{
                  ...styles.dayCell,
                  ...(isCurrentMonth ? {} : styles.dayCellOtherMonth),
                  ...(isToday ? styles.dayCellToday : {}),
                  ...(isSelected ? styles.dayCellSelected : {}),
                }}
              >
                <div style={styles.dayNumber}>{date.getDate()}</div>
                {counts.total > 0 && (
                  <div style={styles.dayCount}>
                    {counts.total}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// WEEK GRID COMPONENT
// =============================================================================

function WeekGrid({
  currentDate,
  selectedDate,
  onSelectDate,
  getCountForDate,
  getEntitiesForDate,
  locale,
}: {
  currentDate: Date;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  getCountForDate: (date: Date) => any;
  getEntitiesForDate: (date: Date) => any;
  locale: string;
}) {
  const today = new Date(getToday());
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

  const weekDays: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(startOfWeek);
    day.setDate(day.getDate() + i);
    weekDays.push(day);
  }

  return (
    <div style={styles.weekGrid}>
      {weekDays.map((date, idx) => {
        const isToday = isSameDay(date, today);
        const isSelected = isSameDay(date, selectedDate);
        const entities = getEntitiesForDate(date);
        const allItems = [...entities.events, ...entities.tasks].slice(0, 5);

        return (
          <div
            key={idx}
            onClick={() => onSelectDate(date)}
            style={{
              ...styles.weekDay,
              ...(isSelected ? styles.weekDaySelected : {}),
            }}
          >
            <div style={{
              ...styles.weekDayHeader,
              ...(isToday ? styles.weekDayHeaderToday : {}),
            }}>
              <div style={styles.weekDayName}>
                {date.toLocaleDateString(locale, { weekday: 'short' })}
              </div>
              <div style={styles.weekDayNumber}>{date.getDate()}</div>
            </div>
            <div style={styles.weekDayContent}>
              {allItems.map((item: any) => (
                <div key={item.id} style={{
                  ...styles.weekDayItem,
                  backgroundColor: item.color || '#3b82f6',
                }}>
                  {item.title || item.name}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// DAY GRID COMPONENT
// =============================================================================

function DayGrid({
  currentDate,
  getEntitiesForDate,
}: {
  currentDate: Date;
  getEntitiesForDate: (date: Date) => any;
}) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const entities = getEntitiesForDate(currentDate);
  const allEvents = [...entities.events, ...entities.tasks];

  return (
    <div style={styles.dayGrid}>
      {hours.map((hour) => {
        const hourEvents = allEvents.filter((e: any) => {
          const eventStart = new Date(e.startAt || e.dueAt || e.startDate);
          return eventStart.getHours() === hour;
        });

        return (
          <div key={hour} style={styles.dayHour}>
            <div style={styles.hourLabel}>
              {hour.toString().padStart(2, '0')}:00
            </div>
            <div style={styles.hourContent}>
              {hourEvents.map((event: any) => (
                <div key={event.id} style={{
                  ...styles.hourEvent,
                  backgroundColor: event.color || '#3b82f6',
                }}>
                  {event.title || event.name}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  // Calendar section
  calendarSection: {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  viewSwitch: {
    display: 'flex',
    gap: '0.25rem',
    background: '#f3f4f6',
    borderRadius: '0.5rem',
    padding: '0.25rem',
  },
  viewButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.75rem',
    border: 'none',
    background: 'transparent',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#6b7280',
    transition: 'all 0.15s',
  },
  viewButtonActive: {
    background: 'white',
    color: '#111827',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  navButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    border: '1px solid #e5e7eb',
    background: 'white',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    color: '#374151',
  },
  todayButton: {
    padding: '0.5rem 1rem',
    border: '1px solid #e5e7eb',
    background: 'white',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#374151',
  },
  displaySwitch: {
    display: 'flex',
    gap: '0.25rem',
    background: '#f3f4f6',
    borderRadius: '0.5rem',
    padding: '0.25rem',
  },
  displayButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.75rem',
    border: 'none',
    background: 'transparent',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#6b7280',
    transition: 'all 0.15s',
  },
  displayButtonActive: {
    background: 'white',
    color: '#111827',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  addEventButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  dateDisplay: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '1rem',
    textTransform: 'capitalize',
  },
  // Month grid
  monthGrid: {
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  weekdayHeaders: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
  },
  weekdayHeader: {
    padding: '0.75rem',
    textAlign: 'center',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  week: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
  },
  dayCell: {
    minHeight: '80px',
    padding: '0.5rem',
    borderRight: '1px solid #e5e7eb',
    borderBottom: '1px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'background 0.15s',
    background: 'white',
  },
  dayCellOtherMonth: {
    background: '#f9fafb',
    color: '#9ca3af',
  },
  dayCellToday: {
    background: '#eff6ff',
  },
  dayCellSelected: {
    background: '#dbeafe',
    outline: '2px solid #3b82f6',
    outlineOffset: '-2px',
  },
  dayNumber: {
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: '0.25rem',
  },
  dayCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '1.25rem',
    height: '1.25rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: '#3b82f6',
    color: 'white',
    borderRadius: '9999px',
    padding: '0 0.375rem',
  },
  // Week grid
  weekGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '0.5rem',
  },
  weekDay: {
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    cursor: 'pointer',
    minHeight: '200px',
  },
  weekDaySelected: {
    outline: '2px solid #3b82f6',
  },
  weekDayHeader: {
    padding: '0.75rem',
    background: '#f9fafb',
    textAlign: 'center',
    borderBottom: '1px solid #e5e7eb',
  },
  weekDayHeaderToday: {
    background: '#3b82f6',
    color: 'white',
  },
  weekDayName: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
  },
  weekDayNumber: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  weekDayContent: {
    padding: '0.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  weekDayItem: {
    padding: '0.25rem 0.5rem',
    fontSize: '0.75rem',
    color: 'white',
    borderRadius: '0.25rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Day grid
  dayGrid: {
    maxHeight: '300px',
    overflow: 'auto',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
  },
  dayHour: {
    display: 'flex',
    borderBottom: '1px solid #e5e7eb',
    minHeight: '60px',
  },
  hourLabel: {
    width: '60px',
    padding: '0.5rem',
    background: '#f9fafb',
    fontSize: '0.75rem',
    color: '#6b7280',
    borderRight: '1px solid #e5e7eb',
    flexShrink: 0,
  },
  hourContent: {
    flex: 1,
    padding: '0.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  hourEvent: {
    padding: '0.375rem 0.5rem',
    fontSize: '0.75rem',
    color: 'white',
    borderRadius: '0.25rem',
  },
  // Gantt placeholder
  ganttPlaceholder: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
    background: '#f9fafb',
    borderRadius: '0.5rem',
    color: '#9ca3af',
    gap: '0.5rem',
  },
  // Detail section
  detailSection: {
    flex: 1,
    overflow: 'auto',
    background: '#f9fafb',
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem 1.5rem',
    background: 'white',
    borderBottom: '1px solid #e5e7eb',
    fontSize: '1.125rem',
    fontWeight: 600,
    color: '#111827',
    textTransform: 'capitalize',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  detailContent: {
    padding: '1.5rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem',
    color: '#9ca3af',
  },
  // Module/subgroup styles (same as TodayView)
  moduleSection: {
    marginBottom: '2rem',
  },
  moduleHeader: {
    marginBottom: '0.75rem',
    paddingBottom: '0.5rem',
    borderBottom: '2px solid #e5e7eb',
  },
  moduleTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  moduleIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  moduleName: {
    fontSize: '1.25rem',
    fontWeight: 700,
    margin: 0,
    letterSpacing: '0.025em',
    textTransform: 'uppercase',
  },
  moduleCount: {
    fontSize: '0.875rem',
    color: '#9ca3af',
    fontWeight: 500,
  },
  subgroupSection: {
    marginBottom: '1rem',
  },
  subgroupHeader: {
    marginBottom: '0.5rem',
    padding: '0.375rem 0.5rem',
    borderRadius: '0.375rem',
  },
  subgroupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
  },
  subgroupIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  subgroupName: {
    fontSize: '1rem',
    fontWeight: 600,
    margin: 0,
    color: '#374151',
  },
  subgroupCount: {
    fontSize: '0.875rem',
    color: '#9ca3af',
  },
  subgroupContent: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
};
